
// This file contains county-specific FAQ data
// This can later be moved to the database if needed

export interface CountyFAQ {
  question: string;
  answer: string;
}

interface CountyFAQs {
  [key: string]: CountyFAQ[];
}

// Default FAQs that will be used if county-specific FAQs are not available
export const defaultFAQs: CountyFAQ[] = [
  {
    question: "How much does a nursing home cost in Ireland?",
    answer: "The average cost of a private nursing home in Ireland ranges from €1,000 to €1,600 per week, though costs can vary significantly by county and facility. HSE-supported homes under the Fair Deal scheme may cost less depending on your income and assets assessment."
  },
  {
    question: "What is the Fair Deal scheme and how does it work?",
    answer: "The Fair Deal (Nursing Home Support Scheme) helps cover nursing home costs for eligible residents. Under this scheme, you contribute based on your financial assessment, and the HSE covers the balance. You can apply through your local HSE office, and most nursing homes in Ireland accept Fair Deal support."
  },
  {
    question: "How do I choose the right nursing home?",
    answer: "Focus on location, care type (e.g., dementia care, respite), facility reputation, staff-to-resident ratios, and available activities. Visit multiple homes if possible, speak with staff and current residents, and review HIQA inspection reports. Grandpal's advisors can provide personalized recommendations based on your specific needs."
  },
  {
    question: "What facilities should I look for in a nursing home?",
    answer: "Look for homes with appropriate medical facilities, comfortable accommodation with accessibility features, suitable dining options, social activities, and outdoor spaces. Consider specialty care needs like dementia support, rehabilitation services, or end-of-life care if required."
  },
  {
    question: "How long are the waiting lists for nursing homes?",
    answer: "Waiting lists vary significantly, from immediate availability to several months depending on the popularity of the home. Public facilities often have longer waiting periods compared to private homes. You can join multiple waiting lists simultaneously to increase your chances of placement."
  }
];

// County-specific FAQs
export const countyFAQs: CountyFAQs = {
  "dublin": [
    {
      question: "How much does a nursing home cost in Dublin?",
      answer: "Dublin has some of the highest nursing home costs in Ireland, with private homes ranging from €1,200 to €1,950 per week. HSE-supported homes under the Fair Deal scheme may cost less based on your financial assessment. Due to Dublin's higher cost of living, expect to pay premium rates for central locations."
    },
    {
      question: "What areas in Dublin have the most nursing homes?",
      answer: "North Dublin and South Dublin have the highest concentration of nursing homes, with significant options in Malahide, Howth, Blackrock, Dun Laoghaire, and Foxrock. City Centre locations typically have fewer but more specialized facilities."
    },
    {
      question: "Are there public transport options to Dublin nursing homes?",
      answer: "Most Dublin nursing homes are well-served by public transport. Many homes in north and south Dublin are accessible by Dublin Bus routes, while homes further from the city may require additional transport. Many facilities also offer dedicated transport for visitors or scheduled outings."
    },
    {
      question: "What specialized care is available in Dublin nursing homes?",
      answer: "Dublin offers the widest range of specialized care in Ireland, including advanced dementia care, Alzheimer's units, palliative care, rehabilitation services, and psychiatric support. Many homes employ specialists like speech therapists, physiotherapists, and occupational therapists."
    },
    {
      question: "How can I arrange tours of Dublin nursing homes?",
      answer: "Most Dublin nursing homes welcome scheduled tours for prospective residents and their families. Contact the facility directly to arrange a visit, or use Grandpal's service to coordinate multiple tours in your preferred locations. Weekend and evening tours may be available by special arrangement."
    }
  ],
  "cork": [
    {
      question: "How much does a nursing home cost in Cork?",
      answer: "The average cost of nursing homes in Cork ranges from €1,050 to €1,550 per week, with Cork City locations typically commanding higher rates than rural areas. The Fair Deal scheme can significantly reduce these costs based on your financial assessment."
    },
    {
      question: "What areas in Cork have the most nursing home options?",
      answer: "Cork City and its suburbs offer the highest concentration of nursing homes, with notable clusters in Douglas, Blackrock, Mahon, and Ballincollig. Rural Cork has fewer but often more spacious facilities, particularly in larger towns like Mallow, Fermoy, Bantry, and Clonakilty."
    },
    {
      question: "Are there specialized dementia care facilities in Cork?",
      answer: "Cork has several dedicated dementia care facilities with specially trained staff and purpose-designed environments. These include secure memory care units and homes offering Sonas therapy, reminiscence programs, and multi-sensory stimulation specifically for residents with dementia or Alzheimer's."
    },
    {
      question: "What activities are typically offered in Cork nursing homes?",
      answer: "Cork nursing homes typically offer diverse activity programs including music therapy (often featuring traditional Cork music), arts and crafts, gardening, gentle exercise classes, spiritual services, and regular outings to local attractions. Many homes also organize visits from local schools and community groups."
    },
    {
      question: "How accessible are Cork nursing homes for visitors?",
      answer: "Most Cork nursing homes are accessible by car with dedicated parking, though public transport options vary significantly between city and rural locations. Cork City homes are generally well-served by bus routes, while county locations may require private transport. Many homes also offer flexible visiting hours to accommodate families."
    }
  ],
  "galway": [
    {
      question: "How much does a nursing home cost in Galway?",
      answer: "Nursing home costs in Galway typically range from €950 to €1,450 per week, with Galway City generally having higher rates than rural locations. The Fair Deal scheme can significantly reduce these costs based on your financial assessment and the specific nursing home."
    },
    {
      question: "What are the differences between Galway City and county nursing homes?",
      answer: "Galway City nursing homes offer proximity to Galway University Hospital and specialist medical services, with excellent urban amenities. County Galway homes often feature more spacious grounds, rural views, and a quieter atmosphere. City homes tend to have higher staffing ratios while county homes may offer more competitive pricing."
    },
    {
      question: "Are there Irish-speaking nursing homes in Galway?",
      answer: "Yes, Galway has several nursing homes with Irish-speaking staff, particularly in Connemara and the Gaeltacht regions. These homes offer residents the opportunity to communicate in their first language and often incorporate Irish cultural traditions, music, and activities into daily routines."
    },
    {
      question: "How do I know if a Galway nursing home meets quality standards?",
      answer: "All registered nursing homes in Galway are inspected by HIQA (Health Information and Quality Authority). You can review detailed inspection reports on the HIQA website. Grandpal also provides summaries of recent inspections and quality ratings to help you make informed decisions about Galway nursing homes."
    },
    {
      question: "What specialized healthcare is available in Galway nursing homes?",
      answer: "Many Galway nursing homes offer specialized healthcare services including dementia care, palliative care, post-stroke rehabilitation, and disability support. Galway's proximity to university teaching hospitals means many homes have visiting specialists and advanced care options not available in other counties."
    }
  ],
  "kerry": [
    {
      question: "How much does a nursing home cost in Kerry?",
      answer: "Nursing home costs in Kerry typically range from €950 to €1,350 per week, with locations in Tralee and Killarney generally commanding higher rates than more rural settings. The Fair Deal scheme can significantly reduce these costs based on your financial assessment."
    },
    {
      question: "Are there nursing homes near major Kerry hospitals?",
      answer: "Yes, several Kerry nursing homes are located within close proximity to University Hospital Kerry in Tralee and community hospitals in Killarney, Dingle, Kenmare, and Listowel. These homes often have established relationships with hospital discharge teams to facilitate smooth transitions from hospital care."
    },
    {
      question: "Do Kerry nursing homes cater to tourists visiting family members?",
      answer: "Many Kerry nursing homes are accustomed to accommodating visiting tourists, with some facilities offering guest rooms or partnership arrangements with nearby B&Bs and hotels. Homes in tourist areas like Killarney, Dingle, and Kenmare have particular experience supporting international visitors seeing their loved ones."
    },
    {
      question: "What outdoor amenities do Kerry nursing homes typically offer?",
      answer: "Kerry nursing homes often feature exceptional outdoor spaces that capitalize on the county's natural beauty, including landscaped gardens, walking paths, and scenic seating areas. Many homes organize supervised outings to Kerry's beaches, national parks, and scenic drives as part of their activity programs."
    },
    {
      question: "Are there Irish-speaking staff in Kerry nursing homes?",
      answer: "Yes, particularly in homes located in or near Kerry's Gaeltacht regions like Dingle Peninsula, many nursing homes have Irish-speaking staff members. This can be particularly important for older residents who may be more comfortable conversing in Irish or who revert to their first language as they age."
    }
  ],
  "limerick": [
    {
      question: "How much does a nursing home cost in Limerick?",
      answer: "The cost of nursing homes in Limerick typically ranges from €950 to €1,400 per week, with city locations generally commanding higher rates than rural settings. Under the Fair Deal scheme, your contribution will be based on your financial assessment rather than the full cost."
    },
    {
      question: "What areas in Limerick have good nursing home options?",
      answer: "Limerick City and its suburbs offer the highest concentration of nursing homes, with notable options in areas like Dooradoyle, Castletroy, and Raheen near University Hospital Limerick. County Limerick has well-regarded facilities in larger towns like Newcastle West, Adare, and Kilmallock."
    },
    {
      question: "How accessible are Limerick nursing homes by public transport?",
      answer: "Limerick City nursing homes are generally well-served by local bus routes. County locations have more limited public transport options, though many homes run their own transport services for visitors and resident outings. Most facilities have ample parking for visitors arriving by car."
    },
    {
      question: "What specialized services are available in Limerick nursing homes?",
      answer: "Many Limerick nursing homes offer specialized services including dementia care, stroke rehabilitation, and palliative care. The county's proximity to University Hospital Limerick means residents often have good access to specialist medical services and consultant visits."
    },
    {
      question: "Are there short-term or respite care options in Limerick?",
      answer: "Yes, numerous Limerick nursing homes offer short-term and respite care options, allowing family caregivers to take breaks while ensuring their loved ones receive professional care. These services typically need to be booked in advance, especially during popular holiday periods."
    }
  ]
};

// Function to get FAQs for a specific county
export function getCountyFAQs(countySlug: string): CountyFAQ[] {
  // Convert to lowercase for case-insensitive matching
  const normalizedSlug = countySlug.toLowerCase();
  
  // Return county-specific FAQs if available, otherwise return default FAQs
  return countyFAQs[normalizedSlug] || defaultFAQs;
}
